import React, { useState, useEffect, useRef } from 'react';
import { Heart, Smile } from 'lucide-react';
import { Link } from 'react-router';

const ServiceIntro = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <div 
      ref={sectionRef}
      className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100"
    >
      {/* Decorative background elements - EXACT SVGs from ServiceInformationSection */}
      <div className="absolute top-0 right-0 w-1/3 h-full opacity-5 pointer-events-none">
        <svg width="100%" height="100%" viewBox="0 0 200 400" xmlns="http://www.w3.org/2000/svg">
          <path d="M150 50C180 80 150 150 180 180C210 210 180 270 150 300C120 330 90 300 60 330C30 360 0 330 0 300V0H300C300 0 270 20 240 30C210 40 120 20 150 50Z" fill="#00B2C2" />
        </svg>
      </div>
      
      <div className="absolute bottom-0 left-0 w-1/4 h-1/2 opacity-5 pointer-events-none">
        <svg width="100%" height="100%" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 150C-10 120 0 70 30 40C60 10 120 0 150 30C180 60 190 120 160 150C130 180 70 190 40 160C10 130 50 180 20 150Z" fill="#A6CE39" />
        </svg>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 flex flex-col lg:flex-row items-center gap-8 lg:gap-12 relative">
        {/* Left Content */}
        <div className={`lg:w-1/2 space-y-6 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
          <div className="inline-block">
            <h1 className="text-4xl md:text-5xl font-serif text-gray-800">
              Comprehensive Dental Care for Every Smile
            </h1>
            <div className="h-1 w-20 bg-teal-500 mt-2 transform transition-transform duration-700 ease-out scale-x-0 origin-left" style={{
              transform: isVisible ? 'scaleX(1)' : 'scaleX(0)',
              transitionDelay: '300ms'
            }}></div>
          </div>
          
          <p className="text-gray-700 text-base md:text-lg">
            From routine exams to advanced surgical procedures, we offer personalized dental services for patients of all ages.
          </p>
          
          <ul className="space-y-4">
            {[
              "Comprehensive dental examinations",
              "Advanced treatment options",
              "Personalized patient care"
            ].map((point, index) => (
              <li key={index} className="flex items-start transition-all duration-500 transform" style={{
                transitionDelay: `${300 + (index * 150)}ms`,
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)'
              }}>
                <span className="flex-shrink-0 w-6 h-6 bg-lime-400 rounded-full flex items-center justify-center mr-3 mt-1">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12L10 17L19 8" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <span className="text-gray-700">{point}</span>
              </li>
            ))}
          </ul>
          
          <div className="pt-2 transition-all duration-700" style={{
            transitionDelay: '800ms',
            opacity: isVisible ? 1 : 0
          }}>
            <Link 
              to="/schedule-appointment" 
              className="inline-flex items-center px-6 py-3 bg-teal-500 text-white font-medium rounded-lg shadow-md hover:bg-teal-600 transition-colors"
            >
              Schedule Your Visit
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="ml-2">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
        </div>

        {/* Right Image/Graphic */}
        <div className={`lg:w-1/2 relative transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} style={{ transitionDelay: '200ms' }}>
          <div className="relative rounded-xl overflow-hidden shadow-xl">
            {/* Decorative elements */}
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-teal-500 rounded-full opacity-20"></div>
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-lime-400 rounded-full opacity-20"></div>
            
            <div className="aspect-w-16 aspect-h-9 relative z-10 bg-gray-100 flex items-center justify-center">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 400 300" 
                className="w-full h-full text-teal-500 opacity-70"
              >
                <path 
                  fill="currentColor" 
                  d="M200 50C250 100 300 150 250 200C200 250 150 200 100 200C50 200 50 250 100 250C150 250 200 200 200 150C200 100 150 50 200 50Z" 
                />
              </svg>
            </div>
          </div>
          
          {/* Service tag */}
          <div className="bg-white px-4 py-2 rounded-lg shadow-md absolute bottom-4 left-4 flex items-center space-x-2 z-20">
            <div className="w-3 h-3 rounded-full bg-lime-400"></div>
            <span className="text-sm font-medium text-gray-700">Expert Dental Care</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceIntro;